import { useCallback, useMemo, useRef, useState } from 'react';
import { Plus, Send } from 'lucide-react';
import {
  ExpensePhase,
  formatCurrency,
  itemsReconciliation,
  localDayKey,
  type NeedSummary,
  type Quote,
  type User,
} from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { TabNav } from '../common/TabNav';
import { StatusBadge } from '../common/EntityList';
import { ItemizedListView } from '../planning/ItemizedListView';
import { ItemsReconciliationNote } from '../planning/ItemsReconciliationNote';
import { ExpenseDetailPanel } from '../expenses/ExpenseDetailPanel';
import { ExpenseDocumentsSection } from '../expenses/ExpenseDocumentsSection';
import { LineMoneyPanel } from '../expenses/money/LineMoneyPanel';
import { useExpenseItem } from '../../hooks/useExpenses';
import { useDialog } from '../../context/DialogContext';
import { useCan } from '../../hooks/useCan';
import { usePlanningLists } from '../../hooks/usePlanning';
import { useDeclineQuote, useNeedQuotes, useWithdrawQuote } from '../../hooks/useNeeds';
import { extractApiErrorMessage } from '../../utils/apiError';
import { QuoteComparison } from './QuoteComparison';
import { QuoteEntryModal } from './QuoteEntryModal';
import { AcceptQuoteModal } from './AcceptQuoteModal';
import { RequestQuotesSheet } from './RequestQuotesSheet';
import { BookNeedModal } from './BookNeedModal';
import { NeedConversations } from './NeedConversations';
import { NEED_STAGE_COLORS, stageLabel } from './needDisplay';

export const NEED_SHEET_TABS = [
  'quotes',
  'money',
  'items',
  'details',
  'files',
  'conversations',
] as const;
export type NeedSheetTab = (typeof NEED_SHEET_TABS)[number];

export function isNeedSheetTab(value: string | null): value is NeedSheetTab {
  return !!value && (NEED_SHEET_TABS as readonly string[]).includes(value);
}

export interface NeedSheetProps {
  eventId: string;
  eventName: string;
  /** The need open; null closes the sheet. */
  need: NeedSummary | null;
  tab: NeedSheetTab;
  onTabChange: (tab: NeedSheetTab) => void;
  onClose: () => void;
  currentUser?: User;
}

/**
 * One need, whole: the quotes side by side (and asking for more), its items,
 * its details, its files and the candidates' conversations. The tab lives in
 * the address (`?need=<id>&tab=`), so a link opens it where it was.
 */
export function NeedSheet({ need, onClose, ...rest }: NeedSheetProps) {
  const dirty = useRef(false);
  const { confirm } = useDialog();
  const close = useCallback(async () => {
    if (
      dirty.current &&
      !(await confirm({
        title: 'Unsaved changes',
        message: 'You have unsaved changes. Discard them?',
        confirmLabel: 'Discard',
        variant: 'warning',
      }))
    ) {
      return;
    }
    dirty.current = false;
    onClose();
  }, [confirm, onClose]);

  return (
    <EntityModal
      open={need !== null}
      onClose={() => void close()}
      title={need?.itemName || 'Need'}
      size="xl"
      fullHeight
    >
      {need && (
        <NeedSheetBody
          key={need.id}
          need={need}
          onUnsavedChange={(d) => (dirty.current = d)}
          onDetailsClosed={() => void close()}
          {...rest}
        />
      )}
    </EntityModal>
  );
}

function NeedSheetBody({
  eventId,
  eventName,
  need,
  tab,
  onTabChange,
  currentUser,
  onUnsavedChange,
  onDetailsClosed,
}: Omit<NeedSheetProps, 'need' | 'onClose'> & {
  need: NeedSummary;
  onUnsavedChange: (dirty: boolean) => void;
  onDetailsClosed: () => void;
}) {
  const { data: quotes = [], isLoading } = useNeedQuotes(need.id);
  const { data: lists = [] } = usePlanningLists({ eventId });
  const items = useMemo(
    () => lists.flatMap((l) => l.items).filter((i) => i.expenseItemId === need.id),
    [lists, need.id],
  );
  const canAsk = useCan('messages.vendors');
  const { confirm, alert } = useDialog();
  const decline = useDeclineQuote(need.id);
  const withdraw = useWithdrawQuote(need.id);
  const booked = need.booked !== null;

  const [asking, setAsking] = useState(false);
  const [entry, setEntry] = useState<{ quote: Quote | null } | null>(null);
  const [accepting, setAccepting] = useState<Quote | null>(null);
  const [booking, setBooking] = useState(false);

  const close = async (q: Quote, kind: 'decline' | 'withdraw') => {
    const ok = await confirm({
      title: kind === 'decline' ? 'Decline quote' : 'Vendor dropped out',
      message:
        kind === 'decline'
          ? `Decline ${q.vendorName}’s quote? It stays here for reference; nobody is told.`
          : `Mark ${q.vendorName} as having dropped out?`,
      confirmLabel: kind === 'decline' ? 'Decline' : 'Mark withdrawn',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await (kind === 'decline' ? decline : withdraw).mutateAsync(q.id);
    } catch (err) {
      await alert({
        title: 'Not saved',
        message: extractApiErrorMessage(err, 'Could not update the quote'),
        variant: 'error',
      });
    }
  };

  const money = (n: number) => formatCurrency(n);
  const headline = booked
    ? `${need.booked!.vendorName ?? 'Booked'} · ${money(need.booked!.price)}`
    : need.quotes.lowest !== null
      ? `Best quote ${money(need.quotes.lowest)} · ${need.quotes.lowestVendorName}`
      : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
        <span>{need.vendorCategoryName}</span>
        <StatusBadge label={stageLabel(need.stage)} color={NEED_STAGE_COLORS[need.stage]} />
        {need.budgetAmount !== null && <span>Budget {money(need.budgetAmount)}</span>}
        {headline && <span className="font-medium text-gray-900">{headline}</span>}
      </div>

      <TabNav
        ariaLabel="Need"
        activeId={tab}
        onSelect={(id) => onTabChange(id as NeedSheetTab)}
        items={[
          { id: 'quotes', label: 'Quotes', badge: need.quotes.asked || null },
          { id: 'money', label: 'Money' },
          { id: 'items', label: 'Items', badge: need.itemCount || null },
          { id: 'details', label: 'Details' },
          { id: 'files', label: 'Files' },
          { id: 'conversations', label: 'Conversations' },
        ]}
      />

      {tab === 'quotes' && (
        <div className="space-y-4">
          {!booked && (
            <div className="flex flex-wrap gap-2">
              {canAsk && (
                <button
                  type="button"
                  onClick={() => setAsking(true)}
                  className="min-h-11 sm:min-h-9 px-3 inline-flex items-center gap-1 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700"
                >
                  <Send size={14} /> Ask for quotes
                </button>
              )}
              <button
                type="button"
                onClick={() => setEntry({ quote: null })}
                className="min-h-11 sm:min-h-9 px-3 inline-flex items-center gap-1 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
              >
                <Plus size={14} /> Add a quote I received
              </button>
              <button
                type="button"
                onClick={() => setBooking(true)}
                className="min-h-11 sm:min-h-9 px-3 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
              >
                Book directly
              </button>
            </div>
          )}
          {isLoading ? (
            <div className="h-24 rounded-lg bg-gray-100 animate-pulse" />
          ) : (
            <QuoteComparison
              quotes={quotes}
              items={items}
              budget={need.budgetAmount}
              today={localDayKey()}
              readOnly={booked}
              onAccept={setAccepting}
              onRecord={(quote) => setEntry({ quote })}
              onDecline={(q) => void close(q, 'decline')}
              onWithdraw={(q) => void close(q, 'withdraw')}
            />
          )}
        </div>
      )}

      {tab === 'money' && <NeedMoney need={need} />}

      {tab === 'items' && (
        <div className="space-y-3">
          <ItemsReconciliationNote
            lineId={need.id}
            reconciliation={itemsReconciliation({
              booked: need.phase === ExpensePhase.BOOKED,
              itemsTotal: need.itemsEstimate,
              budget: need.budgetAmount,
              price: need.booked?.price ?? null,
            })}
          />
          <ItemizedListView eventId={eventId} initialFilters={{ expenseItemIds: [need.id] }} />
        </div>
      )}

      {tab === 'details' &&
        (currentUser ? (
          <ExpenseDetailPanel
            expenseItemId={need.id}
            onClose={onDetailsClosed}
            onUnsavedChange={onUnsavedChange}
            currentUser={currentUser}
            showMoney={false}
          />
        ) : null)}

      {tab === 'files' && (
        <div className="space-y-6">
          {/* Every quote's file is filed on the need as well, the vendors' uploads included. */}
          <ExpenseDocumentsSection expenseItemId={need.id} />
        </div>
      )}

      {tab === 'conversations' && <NeedConversations eventId={eventId} quotes={quotes} />}

      <RequestQuotesSheet
        open={asking}
        eventId={eventId}
        eventName={eventName}
        need={need}
        quotes={quotes}
        onClose={() => setAsking(false)}
      />
      <QuoteEntryModal
        open={entry !== null}
        eventId={eventId}
        needId={need.id}
        vendorCategoryId={need.vendorCategoryId}
        quote={entry?.quote ?? null}
        items={items}
        onClose={() => setEntry(null)}
      />
      <AcceptQuoteModal
        needId={need.id}
        quote={accepting}
        quotes={quotes}
        onClose={() => setAccepting(null)}
      />
      <BookNeedModal need={booking ? need : null} onClose={() => setBooking(false)} />
    </div>
  );
}

/**
 * A need's money. Booked: its price, schedule, payments and "Record payment"
 * (`LineMoneyPanel`, the same as the expense editor's). Still sourcing: the
 * budget against the spread of quotes — there is nothing to pay yet.
 */
function NeedMoney({ need }: { need: NeedSummary }) {
  const { data: item, isLoading } = useExpenseItem(need.booked ? need.id : '');
  const money = (n: number) => formatCurrency(n);
  if (!need.booked) {
    const q = need.quotes;
    return (
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div className="rounded-md bg-gray-50 px-3 py-2">
          <dt className="text-xs text-gray-500">Budget</dt>
          <dd className="font-medium text-gray-900">
            {need.budgetAmount !== null ? money(need.budgetAmount) : 'Not set'}
          </dd>
        </div>
        <div className="rounded-md bg-gray-50 px-3 py-2">
          <dt className="text-xs text-gray-500">Quotes back</dt>
          <dd className="font-medium text-gray-900">
            {q.lowest === null
              ? 'None yet'
              : q.highest !== null && q.highest !== q.lowest
                ? `${money(q.lowest)} – ${money(q.highest)}`
                : money(q.lowest)}
          </dd>
        </div>
        <p className="sm:col-span-2 text-gray-500">
          Nothing to pay until a quote is accepted or the need is booked directly.
        </p>
      </dl>
    );
  }
  if (isLoading || !item) return <div className="h-24 rounded-lg bg-gray-100 animate-pulse" />;
  return <LineMoneyPanel item={item} />;
}
